import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import { useFeatureFlagSplit } from '@verantos-dev/app-state-base';
import {
  selectHasStagedChanges,
  selectLatestCohortVersionId,
  selectLatestDatasetReleaseId,
  selectLatestStatus,
  useCohortDefinitionStore,
} from '@verantos-dev/app-state-cohort-definition';
import {
  type ProcessCohortInput,
  useCohortProfileQuery,
  useGenerateSQLTranslationMutation,
  useSaveCohortVersionMutation,
} from '@verantos-dev/data-access-cohorts';
import { avKeys, avToServer } from '@verantos-dev/feature-analytical-variables';
import { CohortDefinitionStatus, SelectStatementType } from '@verantos-dev/types-cohorts';

import { useClearCohortResultsCache } from './useClearCohortResultsCache';

/** Strip UI-only changeStatus/changedBy before persisting to the server. */

const stripChangeStatus = <T,>({
  changeStatus,
  changedBy,
  ...rest
}: T & { changeStatus?: unknown; changedBy?: unknown }): Omit<T, 'changeStatus' | 'changedBy'> =>
  rest as Omit<T, 'changeStatus' | 'changedBy'>;

/**
 * Caller-supplied execution trigger — typed against TanStack's
 * `mutateAsync` signature from useProcessCohortMutation. The orchestrator
 * stays agnostic to whether the caller's mutation is RTK or TanStack
 * (any async function with the same input shape works).
 */
type PostCohortExecution = (input: ProcessCohortInput) => Promise<unknown>;

export interface UseCohortBuilderSaveQueryOptions {
  /**
   * Whether to keep the SQL translation continuously fresh in the background
   * (fires `generateSQLTranslation` on every `jsonDsl` change) so `submitVersion`
   * always has an up-to-date `sqlValue` ready the instant it's called.
   *
   * Defaults to `true` — every existing caller (the Process/Save confirmation
   * modals) is only ever mounted for the narrow window its own modal is open,
   * so it always wants this.
   */
  // TODO: cleanup with skip_process_confirmation_modal ff
  eagerTranslation?: boolean;
}

export const useCohortBuilderSaveQuery = (
  postCohortExecution?: PostCohortExecution,
  options?: UseCohortBuilderSaveQueryOptions
) => {
  const eagerTranslation = options?.eagerTranslation ?? true;
  const isRaGen3Enabled = useFeatureFlagSplit('research_assistant_gen_3', false);
  // TODO: cleanup with skip_process_confirmation_modal ff
  const isInlineProcessEnabled = Boolean(useFeatureFlagSplit('skip_process_confirmation_modal'));
  const { projectId, cohortId, cohortVersionId } = useParams();
  const queryClient = useQueryClient();
  const [isTranslated, setIsTranslated] = useState(false);
  const [translationFailed, setTranslationFailed] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const { clearCohortResultsCache } = useClearCohortResultsCache(projectId, cohortId);

  // Zustand reads (always called — hooks rules)
  const zustandAVs = useCohortDefinitionStore((s) => s.analyticalVariables);
  const zustandCriteria = useCohortDefinitionStore((s) => s.criteria);
  const zustandStudyPeriod = useCohortDefinitionStore((s) => s.studyPeriod);
  const zustandSimpleEvents = useCohortDefinitionStore((s) => s.simpleEvents);
  const zustandDerivedEvents = useCohortDefinitionStore((s) => s.derivedEvents);
  const zustandIndexDate = useCohortDefinitionStore((s) => s.indexDate);
  const zustandSubjectVariables = useCohortDefinitionStore((s) => s.subjectVariables);
  const zustandLatestStatus = useCohortDefinitionStore(selectLatestStatus);
  const zustandLatestCohortVersionId = useCohortDefinitionStore(selectLatestCohortVersionId);
  const zustandLatestDatasetReleaseId = useCohortDefinitionStore(selectLatestDatasetReleaseId);

  const {
    data: cohortData,
    error: cohortDataError,
    isLoading: isCohortDataLoading,
  } = useCohortProfileQuery({
    projectId: projectId || '',
    cohortId: cohortId || '',
  });

  const [generateSQLTranslation, { data: sqlTranslationData }] =
    useGenerateSQLTranslationMutation();

  // changeStatus/changedBy are UI-only fields and must not be persisted to the server.
  const jsonDsl = useMemo(
    () => ({
      simpleEvents: zustandSimpleEvents.map(stripChangeStatus),
      derivedEvents: zustandDerivedEvents.map(stripChangeStatus),
      indexDate: zustandIndexDate ? stripChangeStatus(zustandIndexDate) : undefined,
      subjectVariables: zustandSubjectVariables.map(stripChangeStatus),
      analyticalVariables: zustandAVs.map(avToServer),
      criteria: zustandCriteria.map((c) => ('changeStatus' in c ? stripChangeStatus(c) : c)),
      studyPeriodRestrictions: {
        overallStudyPeriodRestriction: zustandStudyPeriod.overallStudyPeriodRestriction,
        baselinePeriodRestriction: undefined,
        followUpPeriodRestriction: undefined,
      },
    }),
    [
      zustandAVs,
      zustandCriteria,
      zustandStudyPeriod,
      zustandSimpleEvents,
      zustandDerivedEvents,
      zustandIndexDate,
      zustandSubjectVariables,
    ]
  );

  useEffect(() => {
    if (eagerTranslation && projectId && cohortId && jsonDsl) {
      setTranslationFailed(false);
      setIsTranslated(false);
      (async () => {
        try {
          await generateSQLTranslation({
            cohortJsonDsl: jsonDsl,
            cohortId,
            projectId,
            selectStmtType: SelectStatementType.ALL,
          });
          setIsTranslated(true);
        } catch (e) {
          console.error('Error generating SQL translation', e);
          setTranslationFailed(true);
        }
      })();
    }
  }, [eagerTranslation, cohortId, generateSQLTranslation, projectId, jsonDsl]);

  // Single TanStack mutation handles both POST (new version, for ERROR /
  // PROCESSED statuses) and PUT (update existing, for DRAFT / UNSAVED) —
  // picks the right HTTP method internally based on whether versionId is
  // provided.
  const {
    mutateAsync: saveCohortVersion,
    isPending: isSavingCohortVersion,
    error: saveCohortVersionError,
    data: saveCohortVersionData,
    isSuccess: saveCohortVersionSuccess,
  } = useSaveCohortVersionMutation();

  // Always read from Zustand — it's kept in sync for both flag paths
  const latestStatus = zustandLatestStatus;
  const latestCohortVersionId = zustandLatestCohortVersionId ?? cohortData?.latestCohortVersionId;

  /**
   * Apply optimistic updates BEFORE the execution request. Returns the prior
   * status so callers can hand it to `handleExecutionError` for rollback.
   *
   * Note: TQ cache is intentionally NOT patched. The TQ profile query feeds
   * setProfileStatusFromServer (gated), so patching it with PROCESSING would
   * prematurely clear the execution gate, allowing the next real poll (which
   * may still return DRAFT) to overwrite the optimistic status.
   */
  const applyOptimisticUpdates = useCallback((): {
    priorStatus: CohortDefinitionStatus | undefined;
    priorHasStagedChanges: boolean;
  } => {
    const store = useCohortDefinitionStore.getState();
    const priorStatus = selectLatestStatus(store);
    const priorHasStagedChanges = selectHasStagedChanges(store);
    store.setExecutionPending(true);
    store.setProfileStatus(CohortDefinitionStatus.PROCESSING);
    // The run commits any staged draft server-side, so clear the now-stale flag
    // (pill drops; PROCESSING gates re-staging). Rolled back on error (OR-6158).
    store.setHasStagedChanges(false);
    return { priorStatus, priorHasStagedChanges };
  }, []);

  /**
   * Clear the execution gate and roll back the optimistic PROCESSING status +
   * staged-changes flag on execution error — the run failed, so the prior
   * "Processing…" header and "Unsaved changes" pill must be restored.
   */
  const handleExecutionError = useCallback(
    (rollback?: { priorStatus?: CohortDefinitionStatus; priorHasStagedChanges?: boolean }) => {
      const store = useCohortDefinitionStore.getState();
      store.setExecutionPending(false);
      if (rollback?.priorStatus) {
        store.setProfileStatus(rollback.priorStatus);
      }
      if (rollback?.priorHasStagedChanges) {
        store.setHasStagedChanges(true);
      }
      if (latestCohortVersionId) {
        queryClient.invalidateQueries({ queryKey: avKeys.profile(latestCohortVersionId) });
      }
    },
    [latestCohortVersionId, queryClient]
  );

  /**
   * This function is used to submit the cohort version
   * based on the latest status of the cohort
   *
   * It will create a new version if the status is Processed or Error
   * It will update the existing version if the status is Draft
   * Otherwise, it will do nothing
   * @returns void
   */
  const submitVersion = useCallback(async () => {
    if (!cohortData || !projectId || !cohortId || !jsonDsl || !zustandLatestDatasetReleaseId)
      return;

    switch (latestStatus) {
      case CohortDefinitionStatus.FINAL:
      case CohortDefinitionStatus.PROCESSING: {
        return;
      }
      case CohortDefinitionStatus.ERROR:
      case CohortDefinitionStatus.PROCESSED: {
        let rollback:
          | { priorStatus?: CohortDefinitionStatus; priorHasStagedChanges?: boolean }
          | undefined;
        try {
          setIsSubmitted(true);
          if (postCohortExecution) {
            rollback = applyOptimisticUpdates();
            await postCohortExecution({
              projectId,
              profileId: cohortId,
              versionId: latestCohortVersionId,
              datasetReleaseId: zustandLatestDatasetReleaseId,
              dslVersion: isRaGen3Enabled ? 'v3.0' : 'v0',
              sqlValue: sqlTranslationData?.data?.sqlQuery || '',
              jsonValue: jsonDsl,
            });
            clearCohortResultsCache();
            // Execution runs against the existing version — its `avKeys` cache
            // entry (hasStagedChanges) is otherwise never invalidated by
            // `useProcessCohortMutation`, so it re-hydrates stale on next mount.
            if (latestCohortVersionId) {
              void queryClient.invalidateQueries({ queryKey: avKeys.list(latestCohortVersionId) });
            }
            // TODO: cleanup with skip_process_confirmation_modal ff
            if (isInlineProcessEnabled && latestCohortVersionId) {
              void queryClient.invalidateQueries({
                queryKey: avKeys.profile(latestCohortVersionId),
              });
            }
          } else {
            const result = await saveCohortVersion({
              projectId,
              profileId: cohortId,
              dslVersion: latestCohortVersionId ?? '',
              sqlValue: sqlTranslationData?.data?.sqlQuery || '',
              jsonValue: jsonDsl,
            });
            if (!result.data?.cohortVersionId) {
              console.error('Error creating cohort version');
              return;
            }
            void queryClient.invalidateQueries({
              queryKey: avKeys.list(result.data.cohortVersionId),
            });
            // TODO: cleanup with skip_process_confirmation_modal ff
            if (isInlineProcessEnabled) {
              void queryClient.invalidateQueries({
                queryKey: avKeys.profile(result.data.cohortVersionId),
              });
              // This branch mints a NEW version — the currently-mounted query may
              // still be keyed on the pre-save id if the URL hasn't navigated yet.
              if (latestCohortVersionId && latestCohortVersionId !== result.data.cohortVersionId) {
                void queryClient.invalidateQueries({
                  queryKey: avKeys.profile(latestCohortVersionId),
                });
              }
            }
          }
        } catch (e) {
          console.error('Error creating cohort version', e);
          handleExecutionError(rollback);
        }
        break;
      }
      case CohortDefinitionStatus.UNSAVED:
      case CohortDefinitionStatus.DRAFT: {
        let rollback:
          | { priorStatus?: CohortDefinitionStatus; priorHasStagedChanges?: boolean }
          | undefined;
        try {
          setIsSubmitted(true);
          if (postCohortExecution) {
            rollback = applyOptimisticUpdates();
            await postCohortExecution({
              projectId,
              profileId: cohortId,
              datasetReleaseId: zustandLatestDatasetReleaseId,
              versionId: latestCohortVersionId,
              dslVersion: isRaGen3Enabled ? 'v3.0' : 'v0',
              sqlValue: sqlTranslationData?.data?.sqlQuery || '',
              jsonValue: jsonDsl,
            });
            clearCohortResultsCache();
            if (latestCohortVersionId) {
              void queryClient.invalidateQueries({ queryKey: avKeys.list(latestCohortVersionId) });
            }
            // TODO: cleanup with skip_process_confirmation_modal ff
            if (isInlineProcessEnabled && latestCohortVersionId) {
              void queryClient.invalidateQueries({
                queryKey: avKeys.profile(latestCohortVersionId),
              });
            }
          } else {
            const result = await saveCohortVersion({
              projectId,
              profileId: cohortId,
              versionId: latestCohortVersionId ?? '',
              sqlValue: sqlTranslationData?.data?.sqlQuery || '',
              jsonValue: jsonDsl,
            });
            if (!result.data?.cohortVersionId) {
              console.error('Error creating cohort version');
              return;
            }
            void queryClient.invalidateQueries({
              queryKey: avKeys.list(result.data.cohortVersionId),
            });
            // TODO: cleanup with skip_process_confirmation_modal ff
            if (isInlineProcessEnabled) {
              void queryClient.invalidateQueries({
                queryKey: avKeys.profile(result.data.cohortVersionId),
              });
            }
          }
        } catch (e) {
          console.error('Error updating cohort version', e);
          handleExecutionError(rollback);
        }
        break;
      }
      default:
        return;
    }
  }, [
    cohortData,
    projectId,
    cohortId,
    postCohortExecution,
    sqlTranslationData?.data?.sqlQuery,
    jsonDsl,
    clearCohortResultsCache,
    saveCohortVersion,
    latestStatus,
    latestCohortVersionId,
    applyOptimisticUpdates,
    handleExecutionError,
    zustandLatestDatasetReleaseId,
    queryClient,
  ]);

  return {
    projectId,
    cohortId,
    cohortVersionId,
    cohortData,
    initialLoadingError: cohortDataError,
    isInitialLoading: !isSubmitted && (isCohortDataLoading || !isTranslated),
    submitVersion,
    isSubmitting: isSavingCohortVersion,
    submittingError: saveCohortVersionError,
    submitResponseData: saveCohortVersionData,
    isSubmitted,
    isTranslated,
    translationFailed,
    isSubmitSuccess: saveCohortVersionSuccess,
  };
};
